import React, { useState } from 'react';
import { Button, Modal } from "react-bootstrap";
import Input from 'components/Input';
import Select from 'components/Select';
import Checkbox from 'components/Checkbox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createWorkplace } from 'services/workplaces';
import AddressAutocomplete from 'components/AddressAutocomplete';

const US_TIME_ZONES = [
    { label: "Eastern Time (ET) - New York, Miami", value: "America/New_York" },
    { label: "Central Time (CT) - Chicago, Dallas", value: "America/Chicago" },
    { label: "Mountain Time (MT) - Denver, Salt Lake City", value: "America/Denver" },
    { label: "Mountain Time - Arizona (no DST)", value: "America/Phoenix" },
    { label: "Pacific Time (PT) - Los Angeles, Seattle", value: "America/Los_Angeles" },
    { label: "Alaska Time (AKT)", value: "America/Anchorage" },
    { label: "Hawaii Time (HT) - Honolulu", value: "Pacific/Honolulu" },
];

const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const initialForm = {
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    timezone: '',
    weeklyOvertimeHoursStart: '',
    dailyDoubleOTHoursStart: '',
    weeklyDoubleOTHoursStart: '',
    paidMealBreaks: false,
    paidRestBreaks: false,
};

function AddWorkPlacesModal({ open, onClose }) {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: createWorkplace,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workplaces'] });
            setForm(initialForm);
            setErrors({});
            onClose();
        },
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'Workplace name is required.';
        if (!form.addressLine1.trim()) newErrors.addressLine1 = 'Street 1 is required.';
        if (!form.city.trim()) newErrors.city = 'City is required.';
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        mutation.mutate({
            name: form.name,
            active: true,
            address: {
                street1: form.addressLine1,
                street2: form.addressLine2,
                city: form.city,
                state: form.state,
                zipcode: form.zip,
            },
            timeZone: form.timezone || null,
            weeklyOvertimeHoursStart: form.weeklyOvertimeHoursStart ? parseFloat(form.weeklyOvertimeHoursStart) : null,
            dailyDoubleOtHoursStart: form.dailyDoubleOTHoursStart ? parseFloat(form.dailyDoubleOTHoursStart) : null,
            weeklyDoubleOtHoursStart: form.weeklyDoubleOTHoursStart ? parseFloat(form.weeklyDoubleOTHoursStart) : null,
            enablePaidMealBreaks: form.paidMealBreaks,
            enablePaidRestBreaks: form.paidRestBreaks,
        });
    };

    const handleClose = () => {
        setForm(initialForm);
        setErrors({});
        mutation.reset();
        onClose();
    };

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Add Workplace</h4>
                <button type="button" className="close bg-transparent border-0" onClick={handleClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body className='pt-0'>
                <div>
                    <h2 className='sub-title workplace-information-title mb-0'>Workplace Information</h2>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className='row mt-3'>
                        <div className='col-12'>
                            <label>Workplace Name*</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='Enter Name'
                                name='name'
                                value={form.name}
                                onChange={handleChange}
                                error={!!errors.name}
                                errorText={errors.name}
                            />
                        </div>
                        <div className='my-3'>
                            <h2 className='sub-title workplace-information-title mb-0'>Address Information</h2>
                        </div>
                        <div className='col-lg-6'>
                            <AddressAutocomplete
                                label="Street 1"
                                required
                                value={form.addressLine1}
                                onSelect={(a) => setForm(prev => ({ ...prev, addressLine1: a.line1, city: a.city, state: a.state, zip: a.zip }))}
                                onChange={(v) => setForm(prev => ({ ...prev, addressLine1: v }))}
                                error={!!errors.addressLine1}
                                errorText={errors.addressLine1}
                            />
                        </div>
                        <div className='col-lg-6'>
                            <label>Street 2</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='Street 2'
                                name='addressLine2'
                                value={form.addressLine2}
                                onChange={handleChange}
                            />
                        </div>
                        <div className='col-lg-4 mt-3'>
                            <label>City*</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='Enter city'
                                name='city'
                                value={form.city}
                                onChange={handleChange}
                                error={!!errors.city}
                                errorText={errors.city}
                            />
                        </div>
                        <div className='col-lg-4 mt-3'>
                            <label>State</label>
                            <Select
                                fullWidth
                                placeholder='Select State'
                                options={US_STATES}
                                name='state'
                                value={form.state}
                                onChange={handleChange}
                            />
                        </div>
                        <div className='col-lg-4 mt-3'>
                            <label>Zip Code</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='Enter zipcode'
                                name='zip'
                                value={form.zip}
                                onChange={handleChange}
                            />
                        </div>
                        <div className='my-4'>
                            <h2 className='sub-title workplace-information-title mb-0'>Timekeeping Information</h2>
                        </div>
                        <div className='col-lg-6'>
                            <label>Workplace Time zone</label>
                            <Select
                                fullWidth
                                placeholder='Select time zone'
                                options={US_TIME_ZONES}
                                name='timezone'
                                value={form.timezone}
                                onChange={handleChange}
                            />
                        </div>
                        <div className='col-lg-6'>
                            <label>Weekly Overtime Hours Start</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='40'
                                type='number'
                                name='weeklyOvertimeHoursStart'
                                value={form.weeklyOvertimeHoursStart}
                                onChange={handleChange}
                            />
                        </div>
                        <div className='col-lg-6 mt-3'>
                            <label>Daily Double OT Hours Start</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='40'
                                type='number'
                                name='dailyDoubleOTHoursStart'
                                value={form.dailyDoubleOTHoursStart}
                                onChange={handleChange}
                            />
                        </div>
                        <div className='col-lg-6 mt-3'>
                            <label>Weekly Double OT Hours Start</label>
                            <Input
                                variant='flat'
                                fullWidth
                                placeholder='40'
                                type='number'
                                name='weeklyDoubleOTHoursStart'
                                value={form.weeklyDoubleOTHoursStart}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="form-check mt-4">
                        <Checkbox
                            size='small'
                            label='Enable Paid Meal Breaks'
                            name='paidMealBreaks'
                            checked={form.paidMealBreaks}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-check mt-4">
                        <Checkbox
                            size='small'
                            label='Enable Paid Rest Breaks'
                            name='paidRestBreaks'
                            checked={form.paidRestBreaks}
                            onChange={handleChange}
                        />
                    </div>
                    {mutation.isError && (
                        <p className="text-danger mt-3">
                            {mutation.error?.response?.data?.message || 'Failed to add workplace. Please try again.'}
                        </p>
                    )}
                    <Modal.Footer className='border-0 justify-content-start px-0 mb-5'>
                        <Button
                            type='submit'
                            className='button-orange'
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending ? 'Saving...' : 'Add Workplace'}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal.Body>
        </Modal>
    );
}

export default AddWorkPlacesModal;

